/**
 * Definição de todos os steps de teste do API Health Check.
 *
 * Cada step usa os services REAIS do Admin (mesmos imports das pages).
 * A massa é IDÊNTICA à enviada pelos formulários do Admin.
 *
 * Contexto `ctx` armazena IDs criados para uso em steps subsequentes.
 */

import professionalsService from '../../services/professionalsService';
import servicesService from '../../services/servicesService';
import placesService from '../../services/placesService';
import paymentsService from '../../services/paymentsService';
import placeProfessionalsService from '../../services/placeProfessionalsService';
import placeServicesService from '../../services/placeServicesService';
import placeServiceProfessionalsService from '../../services/placeServiceProfessionalsService';
import placeServicePaymentsService from '../../services/placeServicePaymentsService';
import professionalServicePaymentsService from '../../services/professionalServicePaymentsService';
import schedulesService from '../../services/schedulesService';
import personsService from '../../services/personsService';
import pagesService from '../../services/pagesService';
import dashboardService from '../../services/dashboardService';
import entitiesService from '../../services/entitiesService';
import reportsService from '../../services/reportsService';
import usersService from '../../services/usersService';
import scheduleBlocksService from '../../services/scheduleBlocksService';
import cascadeDeleteService from '../../services/cascadeDeleteService';
import invitesService from '../../services/invitesService';
import professionalAccessService from '../../services/professionalAccessService';

import * as V from './validators';

// ============================================================
// FASE 0 - PRE-CHECK (GETs iniciais - deve retornar vazio/válido)
// ============================================================
const PHASE_0_PRECHECK = [
  {
    id: 'pre-professionals-getAll',
    phase: 0,
    phaseName: 'Pre-Check (Estado Inicial)',
    name: 'GET /professionals (deve estar VAZIO)',
    service: 'professionalsService',
    method: 'getAll',
    run: async () => {
      const data = await professionalsService.getAll();
      return Array.isArray(data) ? data : (data?.professionals || []);
    },
    validate: (data) => V.validateEmptyArray(data),
    description: 'Verifica que não existe nenhum profissional antes de iniciar o teste',
  },
  {
    id: 'pre-services-getAll',
    phase: 0,
    phaseName: 'Pre-Check (Estado Inicial)',
    name: 'GET /services (deve estar VAZIO)',
    service: 'servicesService',
    method: 'getAll',
    run: async () => servicesService.getAll(),
    validate: (data) => V.validateEmptyArray(data),
    description: 'Verifica que não existe nenhum serviço antes de iniciar o teste',
  },
  {
    id: 'pre-places-getAll',
    phase: 0,
    phaseName: 'Pre-Check (Estado Inicial)',
    name: 'GET /places (deve estar VAZIO)',
    service: 'placesService',
    method: 'getAll',
    run: async () => placesService.getAll(),
    validate: (data) => V.validateEmptyNormalizedPlaces(data),
    description: 'Verifica que não existe nenhum local antes de iniciar o teste',
  },
  {
    id: 'pre-payments-getAll',
    phase: 0,
    phaseName: 'Pre-Check (Estado Inicial)',
    name: 'GET /payments (globais presentes, sem customizados)',
    service: 'paymentsService',
    method: 'getAll',
    run: async () => paymentsService.getAll(),
    validate: (data) => {
      const v = V.validatePaymentsList(data);
      if (!v.valid) return v;
      const healthCheck = Array.isArray(data) && data.filter(p => p.name?.includes('[HealthCheck]'));
      if (healthCheck && healthCheck.length > 0) {
        v.errors.push(`Encontrado(s) ${healthCheck.length} pagamento(s) de teste residual (\"[HealthCheck]\") antes do início`);
        v.valid = false;
      }
      return v;
    },
    description: 'Verifica que pagamentos globais estão presentes e não há resíduos de testes anteriores',
  },
  {
    id: 'pre-schedules-getAll',
    phase: 0,
    phaseName: 'Pre-Check (Estado Inicial)',
    name: 'GET /schedules (deve estar VAZIO)',
    service: 'schedulesService',
    method: 'getAll',
    run: async () => schedulesService.getAll(),
    validate: (data) => V.validateEmptyArray(data),
    description: 'Verifica que não existe nenhuma agenda antes de iniciar o teste',
  },
  {
    id: 'pre-persons-getAll',
    phase: 0,
    phaseName: 'Pre-Check (Estado Inicial)',
    name: 'GET /persons (deve estar VAZIO)',
    service: 'personsService',
    method: 'getAll',
    run: async () => personsService.getAll(),
    validate: (data) => V.validateEmptyArray(data),
    description: 'Verifica que não existe nenhuma pessoa antes de iniciar o teste',
  },
  {
    id: 'pre-pages-getAll',
    phase: 0,
    phaseName: 'Pre-Check (Estado Inicial)',
    name: 'GET /pages (sem resíduos [HealthCheck])',
    service: 'pagesService',
    method: 'getAll',
    run: async () => pagesService.getAll(),
    validate: (data) => {
      if (!Array.isArray(data)) return { valid: false, errors: ['Esperado array'] };
      const residuals = data.filter(p => p.title?.includes('[HealthCheck]') || p.slug?.includes('healthcheck'));
      return { valid: residuals.length === 0, errors: residuals.map(p => `Resíduo encontrado: ${p.slug}`) };
    },
    description: 'Verifica que não há páginas [HealthCheck] residuais antes de iniciar o teste',
  },
  {
    id: 'pre-entities-getAll',
    phase: 0,
    phaseName: 'Pre-Check (Estado Inicial)',
    name: 'GET /entities (espelho de entidades)',
    service: 'entitiesService',
    method: 'getAll',
    run: async () => entitiesService.getAll(),
    validate: (data) => V.validateEntities(data),
    description: 'Verifica que entities retorna objeto válido',
  },
  {
    id: 'pre-dashboard-stats',
    phase: 0,
    phaseName: 'Pre-Check (Estado Inicial)',
    name: 'GET /dashboard/stats',
    service: 'dashboardService',
    method: 'getStats',
    run: async () => dashboardService.getStats(),
    validate: (data) => V.validateDashboardStats(data),
    description: 'Verifica que dashboard stats retorna objeto válido',
  },
  {
    id: 'pre-users-theme',
    phase: 0,
    phaseName: 'Pre-Check (Estado Inicial)',
    name: 'GET /users/theme',
    service: 'usersService',
    method: 'getTheme',
    run: async () => usersService.getTheme(),
    validate: (data) => V.validateUserTheme(data),
    description: 'Verifica que tema do usuário retorna objeto válido',
  },
  {
    id: 'pre-invites-getAll',
    phase: 0,
    phaseName: 'Pre-Check (Estado Inicial)',
    name: 'GET /invites (deve estar VAZIO)',
    service: 'invitesService',
    method: 'getAll',
    run: async () => invitesService.getAll(),
    validate: (data) => V.validateEmptyArray(data),
    description: 'Verifica que não existem convites antes de iniciar o teste',
  },
];

// ============================================================
// FASE 1 - CRIAÇÃO DE ENTIDADES (exatamente como os forms do Admin)
// ============================================================
const PHASE_1_CREATE = [
  // --- 1.1 Criar Profissional ---
  {
    id: 'create-professional',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'POST /professionals (criar profissional)',
    service: 'professionalsService',
    method: 'create',
    run: async (ctx) => {
      // Payload idêntico ao ProfessionalForm.jsx
      const data = {
        name: '[HealthCheck] Dr. Teste Silva',
        title: 'Dr.',
        specialty: 'Clínica Geral',
        registrationNumber: 'CRM-12345',
        phone: '(11) 99999-0001',
        email: 'healthcheck.prof@test.agendario.com',
        description: 'Profissional criado pelo API Health Check',
        bio: 'Bio do profissional de teste para validação completa do sistema.',
      };
      const result = await professionalsService.create(data);
      ctx.professionalId = result.id;
      ctx.professionalData = result;
      return result;
    },
    validate: (data) => V.validateProfessional(data),
    description: 'Cria profissional com todos os campos do formulário',
  },
  // --- GET após criar profissional ---
  {
    id: 'verify-professional-getById',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'GET /professionals/{id} (verificar criação)',
    service: 'professionalsService',
    method: 'getById',
    run: async (ctx) => professionalsService.getById(ctx.professionalId),
    validate: (data, ctx) => {
      const v = V.validateProfessional(data);
      if (data.name !== '[HealthCheck] Dr. Teste Silva') {
        v.errors.push(`name: esperado "[HealthCheck] Dr. Teste Silva", recebeu "${data.name}"`);
        v.valid = false;
      }
      if (data.specialty !== 'Clínica Geral') {
        v.errors.push(`specialty: esperado "Clínica Geral", recebeu "${data.specialty}"`);
        v.valid = false;
      }
      return v;
    },
    description: 'Verifica que o profissional foi persistido corretamente',
  },
  {
    id: 'verify-professionals-list-after-create',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'GET /professionals (listar após criação)',
    service: 'professionalsService',
    method: 'getAll',
    run: async () => {
      const data = await professionalsService.getAll();
      return Array.isArray(data) ? data : (data?.professionals || []);
    },
    validate: (data, ctx) => {
      const v = V.validateProfessionalsList(data);
      const found = Array.isArray(data) && data.find(p => p.id === ctx.professionalId);
      if (!found) {
        v.errors.push('Profissional criado não encontrado na lista');
        v.valid = false;
      }
      return v;
    },
    description: 'Verifica que o profissional aparece na listagem',
  },

  // --- 1.2 Criar Serviço ---
  {
    id: 'create-service',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'POST /services (criar serviço)',
    service: 'servicesService',
    method: 'create',
    run: async (ctx) => {
      // Payload idêntico ao ServiceForm.jsx
      const data = {
        name: '[HealthCheck] Consulta Geral',
        description: 'Serviço criado pelo API Health Check',
        category: 'Consultas',
        defaultDurationMinutes: 30,
        defaultPrice: 150.00,
      };
      const result = await servicesService.create(data);
      ctx.serviceId = result.id;
      ctx.serviceData = result;
      return result;
    },
    validate: (data) => V.validateService(data),
    description: 'Cria serviço com todos os campos do formulário',
  },
  {
    id: 'verify-service-getById',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'GET /services/{id} (verificar criação)',
    service: 'servicesService',
    method: 'getById',
    run: async (ctx) => servicesService.getById(ctx.serviceId),
    validate: (data) => {
      const v = V.validateService(data);
      if (data.defaultDurationMinutes !== 30) {
        v.errors.push(`defaultDurationMinutes: esperado 30, recebeu ${data.defaultDurationMinutes}`);
        v.valid = false;
      }
      if (data.defaultPrice !== 150) {
        v.errors.push(`defaultPrice: esperado 150, recebeu ${data.defaultPrice}`);
        v.valid = false;
      }
      return v;
    },
    description: 'Verifica que o serviço foi persistido com valores corretos',
  },

  // --- 1.3 Criar Local ---
  {
    id: 'create-place',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'POST /places (criar local)',
    service: 'placesService',
    method: 'create',
    run: async (ctx) => {
      // Payload idêntico ao PlaceForm.jsx
      const data = {
        name: '[HealthCheck] Consultório Teste',
        description: 'Local criado pelo API Health Check',
        email: 'healthcheck.place@test.agendario.com',
        phone: '(11) 3333-0001',
        website: 'https://test.agendario.com.br',
        addressStreet: 'Rua dos Testes',
        addressNumber: '123',
        addressComplement: 'Sala 1',
        addressNeighborhood: 'Centro',
        addressCity: 'São Paulo',
        addressState: 'SP',
        addressZipCode: '01001-000',
      };
      const result = await placesService.create(data);
      ctx.placeId = result.id;
      ctx.placeData = result;
      return result;
    },
    validate: (data) => V.validatePlace(data),
    description: 'Cria local com todos os campos de endereço',
  },
  {
    id: 'verify-place-getById',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'GET /places/{id} (verificar criação)',
    service: 'placesService',
    method: 'getById',
    run: async (ctx) => placesService.getById(ctx.placeId),
    validate: (data) => V.validatePlace(data),
    description: 'Verifica que o local foi persistido',
  },
  {
    id: 'verify-places-list-after-create',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'GET /places (listar após criação)',
    service: 'placesService',
    method: 'getAll',
    run: async () => placesService.getAll(),
    validate: (data, ctx) => {
      const v = V.validatePlacesNormalized(data);
      const found = data.places?.find(p => p.id === ctx.placeId);
      if (!found) {
        v.errors.push('Local criado não encontrado na lista normalizada');
        v.valid = false;
      }
      return v;
    },
    description: 'Verifica que o local aparece na estrutura normalizada',
  },

  // --- 1.4 Criar Pessoa (Cliente) ---
  {
    id: 'create-person',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'POST /persons (criar pessoa/cliente)',
    service: 'personsService',
    method: 'create',
    run: async (ctx) => {
      // Payload idêntico ao PersonForm.jsx
      const data = {
        name: '[HealthCheck] João Cliente Teste',
        email: 'healthcheck.client@test.agendario.com',
        phone: '11988880001',
        cpf: '12345678900',
        birthDate: '1990-05-15T00:00:00.000Z',
        address: 'Rua do Cliente, 456',
        notes: 'Cliente criado pelo API Health Check',
      };
      const result = await personsService.create(data);
      ctx.personId = result.id;
      ctx.personData = result;
      return result;
    },
    validate: (data) => V.validatePerson(data),
    description: 'Cria pessoa/cliente com todos os campos',
  },
  {
    id: 'verify-person-getById',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'GET /persons/{id} (verificar criação)',
    service: 'personsService',
    method: 'getById',
    run: async (ctx) => personsService.getById(ctx.personId),
    validate: (data) => V.validatePerson(data),
    description: 'Verifica que a pessoa foi persistida',
  },

  // --- 1.5 Criar Forma de Pagamento ---
  {
    id: 'create-payment',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'POST /payments (criar forma de pagamento)',
    service: 'paymentsService',
    method: 'create',
    run: async (ctx) => {
      // Payload idêntico ao PaymentMethodsPage.jsx
      const data = {
        type: 4, // Pix
        name: '[HealthCheck] Pix Teste',
        description: 'Forma de pagamento criada pelo Health Check',
        icon: null,
      };
      const result = await paymentsService.create(data);
      ctx.paymentId = result.id;
      ctx.paymentData = result;
      return result;
    },
    validate: (data) => V.validatePayment(data),
    description: 'Cria forma de pagamento customizada (Pix)',
  },

  // --- Verificar payment por ID ---
  {
    id: 'verify-payment-getById',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'GET /payments/{id} (verificar criação)',
    service: 'paymentsService',
    method: 'getById',
    run: async (ctx) => paymentsService.getById(ctx.paymentId),
    validate: (data, ctx) => {
      const v = V.validatePayment(data);
      if (data.id !== ctx.paymentId) {
        v.errors.push(`id: esperado "${ctx.paymentId}", recebeu "${data.id}"`);
        v.valid = false;
      }
      if (!data.name?.includes('[HealthCheck]')) {
        v.errors.push(`name: esperado conter "[HealthCheck]", recebeu "${data.name}"`);
        v.valid = false;
      }
      return v;
    },
    description: 'Verifica que o pagamento criado pode ser buscado por ID',
  },

  // --- 1.6 Vincular Profissional ao Local ---
  {
    id: 'create-place-professional',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'POST /places/{id}/professionals (vincular profissional ao local)',
    service: 'placeProfessionalsService',
    method: 'create',
    run: async (ctx) => {
      const data = { professionalId: ctx.professionalId };
      const result = await placeProfessionalsService.create(ctx.placeId, data);
      ctx.placeProfessionalId = result.id;
      return result;
    },
    validate: (data) => V.validatePlaceProfessional(data),
    description: 'Vincula profissional ao local (PlaceProfessional)',
  },
  {
    id: 'verify-place-professionals-list',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'GET /places/{id}/professionals (verificar vínculo)',
    service: 'placeProfessionalsService',
    method: 'getByPlaceId',
    run: async (ctx) => placeProfessionalsService.getByPlaceId(ctx.placeId),
    validate: (data, ctx) => {
      const v = { valid: true, errors: [] };
      if (!Array.isArray(data)) { v.valid = false; v.errors.push('Esperado array'); return v; }
      const found = data.find(p => p.professionalId === ctx.professionalId);
      if (!found) { v.valid = false; v.errors.push('Profissional não encontrado no local'); }
      return v;
    },
    description: 'Verifica que o profissional está vinculado ao local',
  },

  // --- 1.7 Vincular Serviço ao Local ---
  {
    id: 'create-place-service',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'POST /places/{id}/services (vincular serviço ao local)',
    service: 'placeServicesService',
    method: 'create',
    run: async (ctx) => {
      const data = { serviceId: ctx.serviceId };
      const result = await placeServicesService.create(ctx.placeId, data);
      ctx.placeServiceId = result.id;
      return result;
    },
    validate: (data) => V.validatePlaceService(data),
    description: 'Vincula serviço ao local (PlaceService)',
  },
  {
    id: 'verify-place-services-list',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'GET /places/{id}/services (verificar vínculo)',
    service: 'placeServicesService',
    method: 'getByPlaceId',
    run: async (ctx) => placeServicesService.getByPlaceId(ctx.placeId),
    validate: (data, ctx) => {
      const v = { valid: true, errors: [] };
      if (!Array.isArray(data)) { v.valid = false; v.errors.push('Esperado array'); return v; }
      const found = data.find(s => s.serviceId === ctx.serviceId);
      if (!found) { v.valid = false; v.errors.push('Serviço não encontrado no local'); }
      return v;
    },
    description: 'Verifica que o serviço está vinculado ao local',
  },

  // --- 1.8 Vincular Profissional ao Serviço no Local ---
  {
    id: 'create-place-service-professional',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'POST /places/{id}/services/{id}/professionals (vincular prof ao serviço no local)',
    service: 'placeServiceProfessionalsService',
    method: 'create',
    run: async (ctx) => {
      const data = { placeProfessionalId: ctx.placeProfessionalId };
      const result = await placeServiceProfessionalsService.create(ctx.placeId, ctx.placeServiceId, data);
      ctx.placeServiceProfessionalId = result.id;
      return result;
    },
    validate: (data) => V.validatePlaceServiceProfessional(data),
    description: 'Vincula profissional ao serviço dentro do local',
  },

  // --- 1.9 Vincular Pagamento ao Serviço do Local ---
  {
    id: 'create-place-service-payment',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'POST /places/{id}/services/{id}/payments (vincular pagamento ao serviço)',
    service: 'placeServicePaymentsService',
    method: 'create',
    run: async (ctx) => {
      const data = {
        placeId: ctx.placeId,
        serviceId: ctx.serviceId,
        paymentId: ctx.paymentId,
        defaultPrice: 150.00,
      };
      const result = await placeServicePaymentsService.create(data);
      ctx.placeServicePaymentId = result.id;
      return result;
    },
    validate: (data) => {
      const errors = [];
      if (!data || !data.id) errors.push('id: ausente');
      return { valid: errors.length === 0, errors };
    },
    description: 'Vincula forma de pagamento ao serviço do local',
  },

  // --- 1.10 Profissional seleciona combo (serviço+pagamento) ---
  {
    id: 'create-professional-service-payment',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'POST /professionals/{id}/service-payments (selecionar combo)',
    service: 'professionalServicePaymentsService',
    method: 'create',
    run: async (ctx) => {
      const data = {
        placeProfessionalId: ctx.placeProfessionalId,
        placeServicePaymentId: ctx.placeServicePaymentId,
        durationMinutes: 45,
        customPrice: 120.00,
        notes: '[HealthCheck] Combo selecionado pelo profissional',
      };
      const result = await professionalServicePaymentsService.create(ctx.professionalId, data);
      ctx.professionalServicePaymentId = result.id;
      return result;
    },
    validate: (data) => {
      const errors = [];
      if (!data || !data.id) errors.push('id: ausente');
      return { valid: errors.length === 0, errors };
    },
    description: 'Profissional seleciona combo de serviço+pagamento disponível no local',
  },

  // --- 1.11 Criar Agenda ---
  {
    id: 'create-schedule',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'POST /schedules (criar agenda)',
    service: 'schedulesService',
    method: 'create',
    run: async (ctx) => {
      // Payload idêntico ao ScheduleForm.jsx
      const data = {
        professionalId: ctx.professionalId,
        placeId: ctx.placeId,
        name: '[HealthCheck] Agenda de Teste',
        description: 'Agenda criada pelo API Health Check',
        maxBookingDaysAhead: 30,
        maxAvailableDaysToShow: 7,
        minimumAdvanceBookingHours: 2,
        startDate: null,
        endDate: null,
      };
      const result = await schedulesService.create(data);
      ctx.scheduleId = result.id;
      ctx.scheduleData = result;
      return result;
    },
    validate: (data) => V.validateSchedule(data),
    description: 'Cria agenda com todos os campos do formulário',
  },

  // --- 1.11 Criar Turno da Manhã ---
  {
    id: 'create-shift-morning',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'POST /schedules/{id}/shifts (turno manhã)',
    service: 'schedulesService',
    method: 'addShift',
    run: async (ctx) => {
      // Payload idêntico ao onboardingService e ScheduleForm
      const shiftData = {
        scheduleId: ctx.scheduleId,
        startTime: '09:00',
        endTime: '12:00',
        name: 'Turno da Manhã',
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
        sunday: false,
        serviceIds: [ctx.serviceId],
      };
      const result = await schedulesService.addShift(ctx.scheduleId, shiftData);
      ctx.shiftMorningId = result.id;
      return result;
    },
    validate: (data) => V.validateShift(data),
    description: 'Cria turno da manhã (seg-sex 09:00-12:00)',
  },

  // --- 1.12 Criar Turno da Tarde ---
  {
    id: 'create-shift-afternoon',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'POST /schedules/{id}/shifts (turno tarde)',
    service: 'schedulesService',
    method: 'addShift',
    run: async (ctx) => {
      const shiftData = {
        scheduleId: ctx.scheduleId,
        startTime: '13:00',
        endTime: '18:00',
        name: 'Turno da Tarde',
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
        sunday: false,
        serviceIds: [ctx.serviceId],
      };
      const result = await schedulesService.addShift(ctx.scheduleId, shiftData);
      ctx.shiftAfternoonId = result.id;
      return result;
    },
    validate: (data) => V.validateShift(data),
    description: 'Cria turno da tarde (seg-sex 13:00-18:00)',
  },

  // --- 1.13 Criar Convite ---
  {
    id: 'create-invite',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'POST /invites (criar convite)',
    service: 'invitesService',
    method: 'create',
    run: async (ctx) => {
      const result = await invitesService.create({
        email: 'healthcheck-invite@test.agendario.com',
        context: 'healthcheck',
      });
      ctx.inviteId = result.id;
      return result;
    },
    validate: (data) => V.validateUserInvite(data),
    description: 'Cria convite de teste para o email healthcheck-invite@test.agendario.com',
  },

  // --- Compartilhamento de profissional ---
  {
    id: 'lookup-access-grantee',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'GET /users/lookup (buscar usuário para compartilhamento)',
    service: 'usersService',
    method: 'lookupByEmail',
    run: async (ctx) => {
      try {
        const result = await usersService.lookupByEmail('healthcheck-invite@test.agendario.com');
        ctx.accessGranteeUserId = result.id;
        return result;
      } catch (err) {
        if (err.response?.status === 404) {
          // Usuário de teste não existe neste ambiente — steps de compartilhamento serão ignorados
          ctx.accessGranteeUserId = null;
          return null;
        }
        throw err;
      }
    },
    validate: (data) => {
      if (data === null) return { valid: true, errors: ['[SKIPPED] Usuário healthcheck-invite@test.agendario.com não existe neste ambiente — steps de compartilhamento serão ignorados'] };
      return V.validateUserLookup(data);
    },
    description: 'Busca o usuário healthcheck-invite@test.agendario.com. Se não existir (404), os steps de compartilhamento são ignorados graciosamente.',
  },
  {
    id: 'grant-professional-access',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'POST /professionals/{id}/access (conceder acesso)',
    service: 'professionalAccessService',
    method: 'grantAccess',
    run: async (ctx) => {
      if (!ctx.accessGranteeUserId) return null;
      return professionalAccessService.grantAccess(ctx.professionalId, {
        userId: ctx.accessGranteeUserId,
        permissions: [
          { entityType: 1, accessLevel: 1 }, // Professional=Read
        ],
      });
    },
    validate: (data) => {
      if (data === null) return { valid: true, errors: ['[SKIPPED] Usuário de compartilhamento não disponível neste ambiente'] };
      return V.validateProfessionalAccessGrantList(data);
    },
    description: 'Concede acesso de leitura ao profissional para o usuário de teste. Ignorado se o usuário lookup não existir.',
  },

  // --- Verificar turnos ---
  {
    id: 'verify-shifts-list',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'GET /schedules/{id}/shifts (verificar turnos)',
    service: 'schedulesService',
    method: 'getShifts',
    run: async (ctx) => schedulesService.getShifts(ctx.scheduleId),
    validate: (data) => {
      const v = V.validateShiftsList(data);
      if (Array.isArray(data) && data.length < 2) {
        v.errors.push(`Esperado pelo menos 2 turnos, encontrou ${data.length}`);
        v.valid = false;
      }
      return v;
    },
    description: 'Verifica que ambos os turnos foram criados',
  },

  // --- 1.14 Buscar template para criar página ---
  {
    id: 'fetch-page-template',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'GET /pages/templates/link-in-bio (buscar template para criar página)',
    service: 'pagesService',
    method: 'getTemplates',
    run: async (ctx) => {
      const result = await pagesService.getTemplates('link-in-bio');
      const arr = Array.isArray(result) ? result : (result?.$values ?? []);
      if (arr.length > 0) { ctx.pageTemplateId = arr[0].id; }
      return arr;
    },
    validate: (data) => {
      const arr = Array.isArray(data) ? data : [];
      if (arr.length === 0) return { valid: false, errors: ['Nenhum template link_in_bio disponível'] };
      return { valid: true, errors: [] };
    },
    description: 'Busca templateId para usar na criação da página',
  },

  // --- 1.15 Criar Página ---
  {
    id: 'create-page',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'POST /pages (criar página)',
    service: 'pagesService',
    method: 'create',
    run: async (ctx) => {
      const ts = Date.now();
      ctx.pageSlug = `healthcheck-pagina-teste-${ts}`;
      const data = {
        type: 'link_in_bio',
        title: '[HealthCheck] Página Teste',
        slug: ctx.pageSlug,
        templateId: ctx.pageTemplateId,
        config: {},
      };
      const result = await pagesService.create(data);
      ctx.pageId = result.id;
      return result;
    },
    validate: (data) => V.validatePageEntity(data),
    description: 'Cria página link_in_bio com template selecionado',
  },
  {
    id: 'verify-page-getById',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'GET /pages/{id} (verificar criação)',
    service: 'pagesService',
    method: 'getById',
    run: async (ctx) => pagesService.getById(ctx.pageId),
    validate: (data) => {
      const v = V.validatePageEntity(data);
      if (data?.title !== '[HealthCheck] Página Teste') {
        v.errors.push(`title: esperado "[HealthCheck] Página Teste", recebeu "${data?.title}"`);
        v.valid = false;
      }
      return v;
    },
    description: 'Verifica que a página foi persistida corretamente',
  },
  {
    id: 'verify-pages-list-after-create',
    phase: 1,
    phaseName: 'Criação de Entidades',
    name: 'GET /pages (listar após criação)',
    service: 'pagesService',
    method: 'getAll',
    run: async () => pagesService.getAll(),
    validate: (data, ctx) => {
      const arr = Array.isArray(data) ? data : [];
      const v = V.validatePagesList(arr);
      if (!arr.find(p => p.id === ctx.pageId)) {
        v.errors.push('Página criada não encontrada na lista');
        v.valid = false;
      }
      return v;
    },
    description: 'Verifica que a página aparece na listagem',
  },
];

// ============================================================
// FASE 2 - VERIFICAÇÃO PÓS-CRIAÇÃO (GETs completos)
// ============================================================
const PHASE_2_VERIFY = [
  {
    id: 'verify-entities-after-create',
    phase: 2,
    phaseName: 'Verificação Pós-Criação',
    name: 'GET /entities (espelho após criação)',
    service: 'entitiesService',
    method: 'getAll',
    run: async () => entitiesService.getAll(),
    validate: (data) => V.validateEntities(data),
    description: 'Verifica espelho completo de entidades após criações',
  },
  {
    id: 'verify-dashboard-stats-after-create',
    phase: 2,
    phaseName: 'Verificação Pós-Criação',
    name: 'GET /dashboard/stats (após criação)',
    service: 'dashboardService',
    method: 'getStats',
    run: async () => dashboardService.getStats(),
    validate: (data) => V.validateDashboardStats(data),
    description: 'Verifica que dashboard reflete as novas entidades',
  },
  {
    id: 'verify-schedules-with-relations',
    phase: 2,
    phaseName: 'Verificação Pós-Criação',
    name: 'GET /schedules?includeDeleted=true (com relações)',
    service: 'schedulesService',
    method: 'getAllWithRelations',
    run: async () => schedulesService.getAllWithRelations(),
    validate: (data, ctx) => {
      const v = V.validateSchedulesWithRelations(data);
      if (Array.isArray(data.schedules)) {
        const found = data.schedules.find(s => s.id === ctx.scheduleId);
        if (!found) {
          v.errors.push('Agenda criada não encontrada nas relações');
          v.valid = false;
        }
      }
      return v;
    },
    description: 'Verifica schedules com profissionais e locais relacionados',
  },
  {
    id: 'verify-places-normalized-after-create',
    phase: 2,
    phaseName: 'Verificação Pós-Criação',
    name: 'GET /places (normalizado após criação)',
    service: 'placesService',
    method: 'getAll',
    run: async () => placesService.getAll(),
    validate: (data, ctx) => {
      const v = V.validatePlacesNormalized(data);
      // Verificar que profissional, serviço e local aparecem na estrutura normalizada
      if (Array.isArray(data.places)) {
        const place = data.places.find(p => p.id === ctx.placeId);
        if (!place) {
          v.errors.push('Local não encontrado na lista normalizada');
          v.valid = false;
        }
      }
      if (Array.isArray(data.professionals)) {
        const prof = data.professionals.find(p => p.id === ctx.professionalId);
        if (!prof) {
          v.errors.push('Profissional não encontrado na lista normalizada de places');
          v.valid = false;
        }
      }
      return v;
    },
    description: 'Verifica estrutura normalizada completa com vínculos',
  },
  {
    id: 'verify-professional-places-schedules',
    phase: 2,
    phaseName: 'Verificação Pós-Criação',
    name: 'GET /professionals/{id}/places-schedules',
    service: 'professionalsService',
    method: 'getPlacesSchedules',
    run: async (ctx) => professionalsService.getPlacesSchedules(ctx.professionalId),
    validate: (data, ctx) => {
      const errors = [];
      if (!Array.isArray(data)) {
        return { valid: false, errors: ['Esperado array de places-schedules'] };
      }
      if (data.length === 0) {
        errors.push('Nenhum place-schedule encontrado para o profissional');
      }
      return { valid: errors.length === 0, errors };
    },
    description: 'Verifica locais e agendas vinculados ao profissional',
  },
  {
    id: 'verify-place-service-professionals-list',
    phase: 2,
    phaseName: 'Verificação Pós-Criação',
    name: 'GET /places/{id}/services/{id}/professionals (verificar vínculo triplo)',
    service: 'placeServiceProfessionalsService',
    method: 'getByPlaceServiceId',
    run: async (ctx) => placeServiceProfessionalsService.getByPlaceServiceId(ctx.placeId, ctx.placeServiceId),
    validate: (data) => {
      const errors = [];
      if (!Array.isArray(data)) return { valid: false, errors: ['Esperado array'] };
      if (data.length === 0) errors.push('Nenhum profissional vinculado ao serviço do local');
      return { valid: errors.length === 0, errors };
    },
    description: 'Verifica vínculo profissional-serviço-local',
  },
  {
    id: 'verify-place-service-payments-list',
    phase: 2,
    phaseName: 'Verificação Pós-Criação',
    name: 'GET /places/{id}/services/{id}/payments (verificar pagamentos do serviço)',
    service: 'placeServicePaymentsService',
    method: 'getByPlaceAndService',
    run: async (ctx) => placeServicePaymentsService.getByPlaceAndService(ctx.placeId, ctx.serviceId),
    validate: (data) => {
      const errors = [];
      if (!Array.isArray(data)) return { valid: false, errors: ['Esperado array'] };
      if (data.length === 0) errors.push('Nenhum pagamento vinculado ao serviço');
      return { valid: errors.length === 0, errors };
    },
    description: 'Verifica pagamentos vinculados ao serviço do local',
  },
  {
    id: 'verify-professional-service-payments-list',
    phase: 2,
    phaseName: 'Verificação Pós-Criação',
    name: 'GET /professionals/{id}/service-payments (listar combos selecionados)',
    service: 'professionalServicePaymentsService',
    method: 'getByProfessionalId',
    run: async (ctx) => professionalServicePaymentsService.getByProfessionalId(ctx.professionalId),
    validate: (data, ctx) => {
      const errors = [];
      if (!Array.isArray(data)) return { valid: false, errors: ['Esperado array'] };
      if (data.length === 0) errors.push('Nenhum combo encontrado para o profissional');
      if (ctx.professionalServicePaymentId && !data.find(c => c.id === ctx.professionalServicePaymentId)) {
        errors.push('Combo criado não encontrado na listagem');
      }
      return { valid: errors.length === 0, errors };
    },
    description: 'Verifica que o combo selecionado aparece na lista do profissional',
  },
  {
    id: 'verify-professional-service-payments-available',
    phase: 2,
    phaseName: 'Verificação Pós-Criação',
    name: 'GET /professionals/{id}/service-payments/available (combos disponíveis)',
    service: 'professionalServicePaymentsService',
    method: 'getAvailableForProfessional',
    run: async (ctx) => professionalServicePaymentsService.getAvailableForProfessional(ctx.professionalId),
    validate: (data) => {
      // Pode estar vazio se o profissional já selecionou todos os combos do local
      if (!Array.isArray(data)) return { valid: false, errors: ['Esperado array de combos disponíveis'] };
      return { valid: true, errors: [] };
    },
    description: 'Verifica endpoint de combos disponíveis para o profissional (pode retornar array vazio)',
  },
  {
    id: 'verify-professional-service-payment-by-id',
    phase: 2,
    phaseName: 'Verificação Pós-Criação',
    name: 'GET /professionals/{id}/service-payments/{id} (buscar por ID)',
    service: 'professionalServicePaymentsService',
    method: 'getById',
    run: async (ctx) => professionalServicePaymentsService.getById(ctx.professionalId, ctx.professionalServicePaymentId),
    validate: (data, ctx) => {
      const errors = [];
      if (!data || !data.id) return { valid: false, errors: ['Combo não encontrado'] };
      if (data.id !== ctx.professionalServicePaymentId) errors.push('ID retornado não corresponde ao criado');
      return { valid: errors.length === 0, errors };
    },
    description: 'Verifica busca por ID do combo selecionado pelo profissional',
  },
  {
    id: 'verify-reports-summary',
    phase: 2,
    phaseName: 'Verificação Pós-Criação',
    name: 'GET /reports/summary (relatório)',
    service: 'reportsService',
    method: 'getSummary',
    run: async () => {
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString();
      return reportsService.getSummary(startDate, endDate);
    },
    validate: (data) => V.validateReportsSummary(data),
    description: 'Verifica que relatório do mês retorna dados válidos',
  },
  {
    id: 'verify-payments-available',
    phase: 2,
    phaseName: 'Verificação Pós-Criação',
    name: 'GET /payments/available (pagamentos disponíveis)',
    service: 'paymentsService',
    method: 'getAvailable',
    run: async () => paymentsService.getAvailable(),
    validate: (data) => V.validatePaymentsList(data),
    description: 'Verifica pagamentos disponíveis para o usuário',
  },
  {
    id: 'verify-payments-global',
    phase: 2,
    phaseName: 'Verificação Pós-Criação',
    name: 'GET /payments/global (pagamentos globais)',
    service: 'paymentsService',
    method: 'getGlobal',
    run: async () => paymentsService.getGlobal(),
    validate: (data) => V.validatePaymentsList(data),
    description: 'Verifica pagamentos globais do sistema',
  },
  {
    id: 'verify-invites-getAll',
    phase: 2,
    phaseName: 'Verificação Pós-Criação',
    name: 'GET /invites (verificar convite criado)',
    service: 'invitesService',
    method: 'getAll',
    run: async () => invitesService.getAll(),
    validate: (data, ctx) => {
      const v = V.validateUserInvitesList(data);
      if (Array.isArray(data) && !data.find(i => i.id === ctx.inviteId)) {
        v.errors.push('Convite criado não encontrado na listagem');
        v.valid = false;
      }
      return v;
    },
    description: 'Verifica que o convite criado aparece na listagem',
  },
  {
    id: 'verify-professional-access-list-after-grant',
    phase: 2,
    phaseName: 'Verificação Pós-Criação',
    name: 'GET /professionals/{id}/access (após grant)',
    service: 'professionalAccessService',
    method: 'getAccessList',
    run: async (ctx) => professionalAccessService.getAccessList(ctx.professionalId),
    validate: (data, ctx) => {
      const v = V.validateProfessionalAccessSummaryList(data);
      if (!v.valid) return v;
      if (ctx.accessGranteeUserId) {
        const found = data.find(a => a.grantedToUserId === ctx.accessGranteeUserId);
        if (!found) {
          v.errors.push(`Usuário ${ctx.accessGranteeUserId} deveria ter acesso mas não aparece na lista`);
          v.valid = false;
        }
      }
      return v;
    },
    description: 'Verifica que o acesso concedido aparece na listagem do profissional',
  },
];

// ============================================================
// FASE 3 - ATUALIZAÇÃO DE ENTIDADES (idêntico aos forms)
// ============================================================
const PHASE_3_UPDATE = [
  {
    id: 'update-professional',
    phase: 3,
    phaseName: 'Atualização de Entidades',
    name: 'PUT /professionals/{id} (atualizar profissional)',
    service: 'professionalsService',
    method: 'update',
    run: async (ctx) => {
      const data = {
        name: '[HealthCheck] Dr. Teste Silva (Atualizado)',
        title: 'Dr.',
        specialty: 'Cardiologia',
        registrationNumber: 'CRM-12345-UPD',
        phone: '(11) 99999-0002',
        email: 'healthcheck.prof.upd@test.agendario.com',
        description: 'Profissional atualizado pelo Health Check',
        bio: 'Bio atualizada.',
      };
      return professionalsService.update(ctx.professionalId, data);
    },
    validate: (data) => {
      const v = V.validateProfessional(data);
      if (data.specialty !== 'Cardiologia') {
        v.errors.push(`specialty: esperado "Cardiologia" após update, recebeu "${data.specialty}"`);
        v.valid = false;
      }
      return v;
    },
    description: 'Atualiza profissional e valida campos alterados',
  },
  {
    id: 'verify-professional-after-update',
    phase: 3,
    phaseName: 'Atualização de Entidades',
    name: 'GET /professionals/{id} (verificar update)',
    service: 'professionalsService',
    method: 'getById',
    run: async (ctx) => professionalsService.getById(ctx.professionalId),
    validate: (data) => {
      const v = V.validateProfessional(data);
      if (!data.name?.includes('Atualizado')) {
        v.errors.push('Nome não reflete atualização');
        v.valid = false;
      }
      return v;
    },
    description: 'Verifica que update persistiu corretamente',
  },
  // Verificar que entidades relacionadas não quebraram
  {
    id: 'verify-schedules-after-prof-update',
    phase: 3,
    phaseName: 'Atualização de Entidades',
    name: 'GET /schedules (após update de profissional)',
    service: 'schedulesService',
    method: 'getAllWithRelations',
    run: async () => schedulesService.getAllWithRelations(),
    validate: (data, ctx) => {
      const v = V.validateSchedulesWithRelations(data);
      const schedule = data.schedules?.find(s => s.id === ctx.scheduleId);
      if (!schedule) {
        v.errors.push('Agenda desapareceu após update do profissional');
        v.valid = false;
      }
      return v;
    },
    description: 'Verifica que agenda não quebrou após update do profissional',
  },

  {
    id: 'update-service',
    phase: 3,
    phaseName: 'Atualização de Entidades',
    name: 'PUT /services/{id} (atualizar serviço)',
    service: 'servicesService',
    method: 'update',
    run: async (ctx) => {
      const data = {
        name: '[HealthCheck] Consulta Geral (Atualizado)',
        description: 'Serviço atualizado pelo Health Check',
        category: 'Consultas Especializadas',
        defaultDurationMinutes: 45,
        defaultPrice: 200.00,
      };
      return servicesService.update(ctx.serviceId, data);
    },
    validate: (data) => {
      const v = V.validateService(data);
      if (data.defaultDurationMinutes !== 45) {
        v.errors.push(`defaultDurationMinutes: esperado 45, recebeu ${data.defaultDurationMinutes}`);
        v.valid = false;
      }
      return v;
    },
    description: 'Atualiza serviço e valida campos alterados',
  },

  {
    id: 'update-place',
    phase: 3,
    phaseName: 'Atualização de Entidades',
    name: 'PUT /places/{id} (atualizar local)',
    service: 'placesService',
    method: 'update',
    run: async (ctx) => {
      const data = {
        name: '[HealthCheck] Consultório Teste (Atualizado)',
        description: 'Local atualizado pelo Health Check',
        email: 'healthcheck.place.upd@test.agendario.com',
        phone: '(11) 3333-0002',
        website: 'https://test-upd.agendario.com.br',
        addressStreet: 'Rua dos Testes Atualizada',
        addressNumber: '456',
        addressComplement: 'Sala 2',
        addressNeighborhood: 'Vila Nova',
        addressCity: 'São Paulo',
        addressState: 'SP',
        addressZipCode: '01002-000',
      };
      return placesService.update(ctx.placeId, data);
    },
    validate: (data) => V.validatePlace(data),
    description: 'Atualiza local com novos dados',
  },

  {
    id: 'update-person',
    phase: 3,
    phaseName: 'Atualização de Entidades',
    name: 'PUT /persons/{id} (atualizar pessoa)',
    service: 'personsService',
    method: 'update',
    run: async (ctx) => {
      const data = {
        name: '[HealthCheck] João Cliente Teste (Atualizado)',
        email: 'healthcheck.client.upd@test.agendario.com',
        phone: '11988880002',
        cpf: '12345678900',
        birthDate: '1990-05-15T00:00:00.000Z',
        address: 'Rua do Cliente Atualizada, 789',
        notes: 'Cliente atualizado pelo Health Check',
      };
      return personsService.update(ctx.personId, data);
    },
    validate: (data) => V.validatePerson(data),
    description: 'Atualiza pessoa/cliente',
  },

  {
    id: 'update-schedule',
    phase: 3,
    phaseName: 'Atualização de Entidades',
    name: 'PUT /schedules/{id} (atualizar agenda)',
    service: 'schedulesService',
    method: 'update',
    run: async (ctx) => {
      const data = {
        professionalId: ctx.professionalId,
        placeId: ctx.placeId,
        name: '[HealthCheck] Agenda de Teste (Atualizada)',
        description: 'Agenda atualizada pelo Health Check',
        maxBookingDaysAhead: 60,
        maxAvailableDaysToShow: 14,
        minimumAdvanceBookingHours: 4,
        startDate: null,
        endDate: null,
      };
      return schedulesService.update(ctx.scheduleId, data);
    },
    validate: (data) => V.validateSchedule(data),
    description: 'Atualiza agenda com novos parâmetros',
  },

  // Verificar tudo após updates
  {
    id: 'update-payment',
    phase: 3,
    phaseName: 'Atualização de Entidades',
    name: 'PUT /payments/{id} (atualizar forma de pagamento)',
    service: 'paymentsService',
    method: 'update',
    run: async (ctx) => {
      const data = {
        name: '[HealthCheck] Pix Teste (Atualizado)',
        description: 'Forma de pagamento atualizada pelo Health Check',
        icon: null,
      };
      return paymentsService.update(ctx.paymentId, data);
    },
    validate: (data) => {
      const v = V.validatePayment(data);
      if (!data.name?.includes('Atualizado')) {
        v.errors.push('name: não reflete atualização');
        v.valid = false;
      }
      return v;
    },
    description: 'Atualiza forma de pagamento e valida campos alterados',
  },
  {
    id: 'verify-payment-after-update',
    phase: 3,
    phaseName: 'Atualização de Entidades',
    name: 'GET /payments/{id} (verificar update)',
    service: 'paymentsService',
    method: 'getById',
    run: async (ctx) => paymentsService.getById(ctx.paymentId),
    validate: (data) => {
      const v = V.validatePayment(data);
      if (!data.name?.includes('Atualizado')) {
        v.errors.push('name: update não foi persistido');
        v.valid = false;
      }
      return v;
    },
    description: 'Verifica que o update do pagamento foi persistido corretamente',
  },

  // Verificar tudo após updates
  {
    id: 'verify-entities-after-updates',
    phase: 3,
    phaseName: 'Atualização de Entidades',
    name: 'GET /entities (espelho após atualizações)',
    service: 'entitiesService',
    method: 'getAll',
    run: async () => entitiesService.getAll(),
    validate: (data) => V.validateEntities(data),
    description: 'Verifica espelho de entidades após todas as atualizações',
  },
  {
    id: 'verify-places-normalized-after-updates',
    phase: 3,
    phaseName: 'Atualização de Entidades',
    name: 'GET /places (normalizado após atualizações)',
    service: 'placesService',
    method: 'getAll',
    run: async () => placesService.getAll(),
    validate: (data) => V.validatePlacesNormalized(data),
    description: 'Verifica estrutura normalizada após atualizações',
  },
  {
    id: 'update-professional-access',
    phase: 3,
    phaseName: 'Atualização de Entidades',
    name: 'PUT /professionals/{id}/access/{userId} (atualizar permissões)',
    service: 'professionalAccessService',
    method: 'updateAccess',
    run: async (ctx) => {
      if (!ctx.accessGranteeUserId) return null;
      return professionalAccessService.updateAccess(ctx.professionalId, ctx.accessGranteeUserId, {
        permissions: [
          { entityType: 1, accessLevel: 2 }, // Professional=ReadWrite
          { entityType: 3, accessLevel: 1 }, // Schedules=Read
        ],
      });
    },
    validate: (data) => {
      if (data === null) return { valid: true, errors: ['[SKIPPED] Usuário de compartilhamento não disponível neste ambiente'] };
      return V.validateProfessionalAccessGrantList(data);
    },
    description: 'Atualiza permissões do usuário de teste. Ignorado se o usuário lookup não existir.',
  },

  // --- 3.x Atualizar Página ---
  {
    id: 'update-page',
    phase: 3,
    phaseName: 'Atualização de Entidades',
    name: 'PUT /pages/{id} (atualizar página)',
    service: 'pagesService',
    method: 'update',
    run: async (ctx) => pagesService.update(ctx.pageId, {
      title: '[HealthCheck] Página Teste (Atualizada)',
      slug: ctx.pageSlug,
      templateId: ctx.pageTemplateId,
      config: {},
    }),
    validate: (data) => {
      const v = V.validatePageEntity(data);
      if (!data?.title?.includes('Atualizada')) {
        v.errors.push('title: não reflete atualização');
        v.valid = false;
      }
      return v;
    },
    description: 'Atualiza página e valida campos alterados',
  },
  {
    id: 'verify-page-after-update',
    phase: 3,
    phaseName: 'Atualização de Entidades',
    name: 'GET /pages/{id} (verificar update)',
    service: 'pagesService',
    method: 'getById',
    run: async (ctx) => pagesService.getById(ctx.pageId),
    validate: (data) => {
      const v = V.validatePageEntity(data);
      if (!data?.title?.includes('Atualizada')) {
        v.errors.push('title: update não foi persistido');
        v.valid = false;
      }
      return v;
    },
    description: 'Verifica que o update da página persistiu corretamente',
  },
  {
    id: 'update-professional-service-payment',
    phase: 3,
    phaseName: 'Atualização de Entidades',
    name: 'PUT /professionals/{id}/service-payments/{id} (atualizar combo)',
    service: 'professionalServicePaymentsService',
    method: 'update',
    run: async (ctx) => {
      const data = {
        durationMinutes: 60,
        customPrice: 180.00,
        notes: '[HealthCheck] Combo atualizado pelo profissional',
      };
      return professionalServicePaymentsService.update(ctx.professionalId, ctx.professionalServicePaymentId, data);
    },
    validate: (data) => {
      const errors = [];
      if (!data || !data.id) errors.push('id: ausente após update');
      return { valid: errors.length === 0, errors };
    },
    description: 'Atualiza duração e preço customizado do combo selecionado pelo profissional',
  },
];

// ============================================================
// FASE 4 - OPERAÇÕES ADICIONAIS (toggle, pages, user prefs)
// ============================================================
const PHASE_4_EXTRAS = [
  {
    id: 'toggle-shift-active',
    phase: 4,
    phaseName: 'Operações Adicionais',
    name: 'PATCH /schedules/{id}/shifts/{id}/toggle-active',
    service: 'schedulesService',
    method: 'toggleShiftActive',
    run: async (ctx) => schedulesService.toggleShiftActive(ctx.scheduleId, ctx.shiftMorningId),
    validate: (data) => V.validateShift(data),
    description: 'Toggle ativo/inativo do turno da manhã',
  },
  {
    id: 'verify-shift-after-toggle',
    phase: 4,
    phaseName: 'Operações Adicionais',
    name: 'GET /schedules/{id}/shifts/{id} (após toggle)',
    service: 'schedulesService',
    method: 'getShiftById',
    run: async (ctx) => schedulesService.getShiftById(ctx.scheduleId, ctx.shiftMorningId),
    validate: (data) => V.validateShift(data),
    description: 'Verifica estado do turno após toggle',
  },
  // Toggle de volta
  {
    id: 'toggle-shift-active-back',
    phase: 4,
    phaseName: 'Operações Adicionais',
    name: 'PATCH toggle-active (reverter)',
    service: 'schedulesService',
    method: 'toggleShiftActive',
    run: async (ctx) => schedulesService.toggleShiftActive(ctx.scheduleId, ctx.shiftMorningId),
    validate: (data) => V.validateShift(data),
    description: 'Reverte toggle do turno da manhã',
  },
  {
    id: 'toggle-person-active',
    phase: 4,
    phaseName: 'Operações Adicionais',
    name: 'PATCH /persons/{id}/toggle-active',
    service: 'personsService',
    method: 'toggleActive',
    run: async (ctx) => personsService.toggleActive(ctx.personId),
    validate: (data) => V.validatePerson(data),
    description: 'Toggle ativo/inativo da pessoa',
  },
  {
    id: 'get-user-notification-prefs',
    phase: 4,
    phaseName: 'Operações Adicionais',
    name: 'GET /users/notifications/preferences',
    service: 'usersService',
    method: 'getNotificationPreferences',
    run: async () => usersService.getNotificationPreferences(),
    validate: (data) => {
      const errors = [];
      if (!data || typeof data !== 'object') errors.push('Esperado objeto');
      return { valid: errors.length === 0, errors };
    },
    description: 'Obtém preferências de notificação',
  },
  {
    id: 'get-page-templates',
    phase: 4,
    phaseName: 'Operações Adicionais',
    name: 'GET /pages/templates/link-in-bio',
    service: 'pagesService',
    method: 'getTemplates',
    run: async () => pagesService.getTemplates('link-in-bio'),
    validate: (data) => {
      const errors = [];
      if (!data) errors.push('Resposta vazia');
      return { valid: errors.length === 0, errors };
    },
    description: 'Obtém templates de páginas',
  },
  {
    id: 'get-page-types',
    phase: 4,
    phaseName: 'Operações Adicionais',
    name: 'GET /pages/templates/types',
    service: 'pagesService',
    method: 'getPageTypes',
    run: async () => pagesService.getPageTypes(),
    validate: (data) => {
      const errors = [];
      if (!Array.isArray(data)) errors.push('Esperado array de tipos');
      return { valid: errors.length === 0, errors };
    },
    description: 'Obtém tipos de páginas disponíveis',
  },
  {
    id: 'get-blocks',
    phase: 4,
    phaseName: 'Operações Adicionais',
    name: 'GET /blocks (ausências)',
    service: 'scheduleBlocksService',
    method: 'getAll',
    run: async () => scheduleBlocksService.getAll(1, 10),
    validate: (data) => {
      const errors = [];
      if (!data || typeof data !== 'object') errors.push('Esperado objeto paginado');
      return { valid: errors.length === 0, errors };
    },
    description: 'Lista ausências (pode estar vazia)',
  },
  {
    id: 'get-available-professionals-for-place',
    phase: 4,
    phaseName: 'Operações Adicionais',
    name: 'GET /places/{id}/professionals/available',
    service: 'placeProfessionalsService',
    method: 'getAvailableForPlace',
    run: async (ctx) => placeProfessionalsService.getAvailableForPlace(ctx.placeId),
    validate: (data) => {
      if (!Array.isArray(data)) return { valid: false, errors: ['Esperado array'] };
      return { valid: true, errors: [] };
    },
    description: 'Lista profissionais disponíveis para o local',
  },
  {
    id: 'get-available-services-for-place',
    phase: 4,
    phaseName: 'Operações Adicionais',
    name: 'GET /places/{id}/services/available',
    service: 'placeServicesService',
    method: 'getAvailableForPlace',
    run: async (ctx) => placeServicesService.getAvailableForPlace(ctx.placeId),
    validate: (data) => {
      if (!Array.isArray(data)) return { valid: false, errors: ['Esperado array'] };
      return { valid: true, errors: [] };
    },
    description: 'Lista serviços disponíveis para o local',
  },
];

// ============================================================
// FASE 5 - CLEANUP / CASCADE DELETE
// ============================================================
const PHASE_5_CLEANUP = [
  // Deletar turnos
  {
    id: 'delete-shift-afternoon',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'DELETE /schedules/{id}/shifts/{id} (turno tarde)',
    service: 'cascadeDeleteService',
    method: 'confirmDelete',
    run: async (ctx) => {
      const path = `/schedules/${ctx.scheduleId}/shifts/${ctx.shiftAfternoonId}`;
      const impact = await cascadeDeleteService.getImpact(path);
      await cascadeDeleteService.confirmDelete(path, impact.confirmHash);
      return { fase1_impacto: impact, fase2_confirmado: '204 No Content' };
    },
    validate: () => V.validateNoContent(),
    description: 'Deleta turno da tarde (cascade 2 fases)',
  },
  {
    id: 'delete-shift-morning',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'DELETE /schedules/{id}/shifts/{id} (turno manhã)',
    service: 'cascadeDeleteService',
    method: 'confirmDelete',
    run: async (ctx) => {
      const path = `/schedules/${ctx.scheduleId}/shifts/${ctx.shiftMorningId}`;
      const impact = await cascadeDeleteService.getImpact(path);
      await cascadeDeleteService.confirmDelete(path, impact.confirmHash);
      return { fase1_impacto: impact, fase2_confirmado: '204 No Content' };
    },
    validate: () => V.validateNoContent(),
    description: 'Deleta turno da manhã (cascade 2 fases)',
  },
  {
    id: 'verify-shifts-after-delete',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'GET /schedules/{id}/shifts (após deletar turnos)',
    service: 'schedulesService',
    method: 'getShifts',
    run: async (ctx) => schedulesService.getShifts(ctx.scheduleId),
    validate: (data, ctx) => {
      if (!Array.isArray(data)) return { valid: false, errors: ['Esperado array de turnos'] };
      const errors = [];
      if (ctx.shiftMorningId && data.find(s => s.id === ctx.shiftMorningId))
        errors.push(`Turno manhã (${ctx.shiftMorningId}) ainda aparece após delete`);
      if (ctx.shiftAfternoonId && data.find(s => s.id === ctx.shiftAfternoonId))
        errors.push(`Turno tarde (${ctx.shiftAfternoonId}) ainda aparece após delete`);
      return { valid: errors.length === 0, errors };
    },
    description: 'Verifica que os turnos deletados não aparecem mais na agenda',
  },

  // Deletar agenda
  {
    id: 'delete-schedule',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'DELETE /schedules/{id}',
    service: 'cascadeDeleteService',
    method: 'confirmDelete',
    run: async (ctx) => {
      const path = `/schedules/${ctx.scheduleId}`;
      const impact = await cascadeDeleteService.getImpact(path);
      await cascadeDeleteService.confirmDelete(path, impact.confirmHash);
      return { fase1_impacto: impact, fase2_confirmado: '204 No Content' };
    },
    validate: () => V.validateNoContent(),
    description: 'Deleta agenda (cascade 2 fases)',
  },

  // Deletar vínculo place-service-professional
  {
    id: 'delete-place-service-professional',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'DELETE /places/{id}/services/{id}/professionals/{id}',
    service: 'cascadeDeleteService',
    method: 'confirmDelete',
    run: async (ctx) => {
      const path = `/places/${ctx.placeId}/services/${ctx.placeServiceId}/professionals/${ctx.placeServiceProfessionalId}`;
      const impact = await cascadeDeleteService.getImpact(path);
      await cascadeDeleteService.confirmDelete(path, impact.confirmHash);
      return { fase1_impacto: impact, fase2_confirmado: '204 No Content' };
    },
    validate: () => V.validateNoContent(),
    description: 'Remove vínculo profissional-serviço-local (cascade 2 fases)',
  },

  // Deletar combo do profissional ANTES de deletar place-service-payment
  // (place-service-payment cascateia soft delete em professional_service_payment)
  {
    id: 'delete-professional-service-payment',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'DELETE /professionals/{id}/service-payments/{id}',
    service: 'cascadeDeleteService',
    method: 'confirmDelete',
    run: async (ctx) => {
      const path = `/professionals/${ctx.professionalId}/service-payments/${ctx.professionalServicePaymentId}`;
      const impact = await cascadeDeleteService.getImpact(path);
      await cascadeDeleteService.confirmDelete(path, impact.confirmHash);
      return { fase1_impacto: impact, fase2_confirmado: '204 No Content' };
    },
    validate: () => V.validateNoContent(),
    description: 'Remove combo selecionado pelo profissional (cascade 2 fases)',
  },
  {
    id: 'verify-professional-service-payments-after-delete',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'GET /professionals/{id}/service-payments (após deletar combo)',
    service: 'professionalServicePaymentsService',
    method: 'getByProfessionalId',
    run: async (ctx) => professionalServicePaymentsService.getByProfessionalId(ctx.professionalId),
    validate: (data, ctx) => {
      const errors = [];
      if (!Array.isArray(data)) return { valid: false, errors: ['Esperado array'] };
      if (ctx.professionalServicePaymentId && data.find(c => c.id === ctx.professionalServicePaymentId)) {
        errors.push('Combo deletado ainda aparece na listagem');
      }
      return { valid: errors.length === 0, errors };
    },
    description: 'Verifica que o combo foi removido da lista do profissional',
  },

  // Deletar vínculo place-service-payment
  {
    id: 'delete-place-service-payment',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'DELETE /places/{id}/services/{id}/payments/{id}',
    service: 'cascadeDeleteService',
    method: 'confirmDelete',
    run: async (ctx) => {
      const path = `/places/${ctx.placeId}/services/${ctx.placeServiceId}/payments/${ctx.placeServicePaymentId}`;
      const impact = await cascadeDeleteService.getImpact(path);
      await cascadeDeleteService.confirmDelete(path, impact.confirmHash);
      return { fase1_impacto: impact, fase2_confirmado: '204 No Content' };
    },
    validate: () => V.validateNoContent(),
    description: 'Remove combo serviço-pagamento do local (cascade 2 fases)',
  },

  // Deletar vínculo place-service
  {
    id: 'delete-place-service',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'DELETE /places/{id}/services/{id}',
    service: 'cascadeDeleteService',
    method: 'confirmDelete',
    run: async (ctx) => {
      const path = `/places/${ctx.placeId}/services/${ctx.serviceId}`;
      const impact = await cascadeDeleteService.getImpact(path);
      await cascadeDeleteService.confirmDelete(path, impact.confirmHash);
      return { fase1_impacto: impact, fase2_confirmado: '204 No Content' };
    },
    validate: () => V.validateNoContent(),
    description: 'Remove vínculo serviço-local (cascade 2 fases)',
  },

  // Deletar vínculo place-professional
  {
    id: 'delete-place-professional',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'DELETE /places/{id}/professionals/{id}',
    service: 'cascadeDeleteService',
    method: 'confirmDelete',
    run: async (ctx) => {
      const path = `/places/${ctx.placeId}/professionals/${ctx.placeProfessionalId}`;
      const impact = await cascadeDeleteService.getImpact(path);
      await cascadeDeleteService.confirmDelete(path, impact.confirmHash);
      return { fase1_impacto: impact, fase2_confirmado: '204 No Content' };
    },
    validate: () => V.validateNoContent(),
    description: 'Remove vínculo profissional-local (cascade 2 fases)',
  },

  // Verificar que vínculos foram removidos
  {
    id: 'verify-place-professionals-after-cleanup',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'GET /places/{id}/professionals (após cleanup)',
    service: 'placeProfessionalsService',
    method: 'getByPlaceId',
    run: async (ctx) => placeProfessionalsService.getByPlaceId(ctx.placeId),
    validate: (data) => V.validateEmptyArray(data),
    description: 'Verifica que não há profissionais no local após cleanup',
  },
  {
    id: 'verify-place-services-after-cleanup',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'GET /places/{id}/services (após cleanup)',
    service: 'placeServicesService',
    method: 'getByPlaceId',
    run: async (ctx) => placeServicesService.getByPlaceId(ctx.placeId),
    validate: (data) => V.validateEmptyArray(data),
    description: 'Verifica que não há serviços no local após cleanup',
  },

  // Deletar entidades principais
  {
    id: 'delete-payment',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'DELETE /payments/{id}',
    service: 'cascadeDeleteService',
    method: 'confirmDelete',
    run: async (ctx) => {
      const path = `/payments/${ctx.paymentId}`;
      const impact = await cascadeDeleteService.getImpact(path);
      await cascadeDeleteService.confirmDelete(path, impact.confirmHash);
      return { fase1_impacto: impact, fase2_confirmado: '204 No Content' };
    },
    validate: () => V.validateNoContent(),
    description: 'Deleta forma de pagamento customizada (cascade 2 fases)',
  },
  {
    id: 'delete-person',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'DELETE /persons/{id}',
    service: 'cascadeDeleteService',
    method: 'confirmDelete',
    run: async (ctx) => {
      const path = `/persons/${ctx.personId}`;
      const impact = await cascadeDeleteService.getImpact(path);
      await cascadeDeleteService.confirmDelete(path, impact.confirmHash);
      return { fase1_impacto: impact, fase2_confirmado: '204 No Content' };
    },
    validate: () => V.validateNoContent(),
    description: 'Deleta pessoa/cliente (cascade 2 fases)',
  },
  {
    id: 'delete-service',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'DELETE /services/{id}',
    service: 'cascadeDeleteService',
    method: 'confirmDelete',
    run: async (ctx) => {
      const path = `/services/${ctx.serviceId}`;
      const impact = await cascadeDeleteService.getImpact(path);
      await cascadeDeleteService.confirmDelete(path, impact.confirmHash);
      return { fase1_impacto: impact, fase2_confirmado: '204 No Content' };
    },
    validate: () => V.validateNoContent(),
    description: 'Deleta serviço (cascade 2 fases)',
  },
  {
    id: 'delete-place',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'DELETE /places/{id}',
    service: 'cascadeDeleteService',
    method: 'confirmDelete',
    run: async (ctx) => {
      const path = `/places/${ctx.placeId}`;
      const impact = await cascadeDeleteService.getImpact(path);
      await cascadeDeleteService.confirmDelete(path, impact.confirmHash);
      return { fase1_impacto: impact, fase2_confirmado: '204 No Content' };
    },
    validate: () => V.validateNoContent(),
    description: 'Deleta local (cascade 2 fases)',
  },
  {
    id: 'revoke-professional-access',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'DELETE /professionals/{id}/access/{userId} (revogar acesso)',
    service: 'professionalAccessService',
    method: 'revokeAccess',
    run: async (ctx) => {
      if (!ctx.accessGranteeUserId) return null;
      await professionalAccessService.revokeAccess(ctx.professionalId, ctx.accessGranteeUserId);
      return null;
    },
    validate: (data, ctx) => {
      if (!ctx.accessGranteeUserId) return { valid: true, errors: ['[SKIPPED] Usuário de compartilhamento não disponível neste ambiente'] };
      return V.validateNoContent();
    },
    description: 'Revoga o acesso concedido ao usuário de teste. Ignorado se o usuário lookup não existir.',
  },
  {
    id: 'verify-professional-access-after-revoke',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'GET /professionals/{id}/access (após revogar — deve estar vazio)',
    service: 'professionalAccessService',
    method: 'getAccessList',
    run: async (ctx) => professionalAccessService.getAccessList(ctx.professionalId),
    validate: (data) => V.validateEmptyArray(data),
    description: 'Verifica que não há acessos ativos após revogar',
  },
  {
    id: 'delete-professional',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'DELETE /professionals/{id}',
    service: 'cascadeDeleteService',
    method: 'confirmDelete',
    run: async (ctx) => {
      const path = `/professionals/${ctx.professionalId}`;
      const impact = await cascadeDeleteService.getImpact(path);
      await cascadeDeleteService.confirmDelete(path, impact.confirmHash);
      return { fase1_impacto: impact, fase2_confirmado: '204 No Content' };
    },
    validate: () => V.validateNoContent(),
    description: 'Deleta profissional (cascade 2 fases)',
  },
  {
    id: 'delete-page',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'DELETE /pages/{id}',
    service: 'cascadeDeleteService',
    method: 'confirmDelete',
    run: async (ctx) => {
      const path = `/pages/${ctx.pageId}`;
      const impact = await cascadeDeleteService.getImpact(path);
      await cascadeDeleteService.confirmDelete(path, impact.confirmHash);
      return { fase1_impacto: impact, fase2_confirmado: '204 No Content' };
    },
    validate: () => V.validateNoContent(),
    description: 'Deleta página (cascade 2 fases)',
  },
  {
    id: 'delete-invite',
    phase: 5,
    phaseName: 'Cleanup (Exclusão)',
    name: 'DELETE /invites/{id} (cancelar convite)',
    service: 'invitesService',
    method: 'cancel',
    run: async (ctx) => invitesService.cancel(ctx.inviteId),
    validate: () => V.validateNoContent(),
    description: 'Cancela convite de teste (soft delete)',
  },
];

// ============================================================
// FASE 6 - VERIFICAÇÃO PÓS-CLEANUP
// ============================================================
const PHASE_6_POST_CLEANUP = [
  {
    id: 'post-professionals-getAll',
    phase: 6,
    phaseName: 'Verificação Pós-Cleanup',
    name: 'GET /professionals (após cleanup)',
    service: 'professionalsService',
    method: 'getAll',
    run: async () => {
      const data = await professionalsService.getAll();
      return Array.isArray(data) ? data : (data?.professionals || []);
    },
    validate: (data, ctx) => {
      const v = V.validateProfessionalsList(data);
      if (Array.isArray(data)) {
        const found = data.find(p => p.id === ctx.professionalId);
        if (found) {
          v.errors.push('Profissional deletado ainda aparece na lista');
          v.valid = false;
        }
      }
      return v;
    },
    description: 'Verifica que profissional foi removido',
  },
  {
    id: 'post-services-getAll',
    phase: 6,
    phaseName: 'Verificação Pós-Cleanup',
    name: 'GET /services (após cleanup)',
    service: 'servicesService',
    method: 'getAll',
    run: async () => servicesService.getAll(),
    validate: (data, ctx) => {
      const v = V.validateServicesList(data);
      if (Array.isArray(data)) {
        const found = data.find(s => s.id === ctx.serviceId);
        if (found) {
          v.errors.push('Serviço deletado ainda aparece na lista');
          v.valid = false;
        }
      }
      return v;
    },
    description: 'Verifica que serviço foi removido',
  },
  {
    id: 'post-places-getAll',
    phase: 6,
    phaseName: 'Verificação Pós-Cleanup',
    name: 'GET /places (após cleanup)',
    service: 'placesService',
    method: 'getAll',
    run: async () => placesService.getAll(),
    validate: (data, ctx) => {
      const v = V.validateEmptyOrNormalizedPlaces(data);
      if (Array.isArray(data.places)) {
        const found = data.places.find(p => p.id === ctx.placeId);
        if (found) {
          v.errors.push('Local deletado ainda aparece na lista');
          v.valid = false;
        }
      }
      return v;
    },
    description: 'Verifica que local foi removido',
  },
  {
    id: 'post-pages-getAll',
    phase: 6,
    phaseName: 'Verificação Pós-Cleanup',
    name: 'GET /pages (após cleanup)',
    service: 'pagesService',
    method: 'getAll',
    run: async () => pagesService.getAll(),
    validate: (data, ctx) => {
      const arr = Array.isArray(data) ? data : [];
      const v = V.validatePagesList(arr);
      if (arr.find(p => p.id === ctx.pageId)) {
        v.errors.push('Página deletada ainda aparece na lista');
        v.valid = false;
      }
      return v;
    },
    description: 'Verifica que a página foi removida',
  },
  {
    id: 'post-entities-getAll',
    phase: 6,
    phaseName: 'Verificação Pós-Cleanup',
    name: 'GET /entities (espelho final)',
    service: 'entitiesService',
    method: 'getAll',
    run: async () => entitiesService.getAll(),
    validate: (data) => V.validateEntities(data),
    description: 'Verifica espelho final de entidades - sem itens de teste',
  },
  {
    id: 'post-payments-getAll',
    phase: 6,
    phaseName: 'Verificação Pós-Cleanup',
    name: 'GET /payments (globais presentes, sem customizados após cleanup)',
    service: 'paymentsService',
    method: 'getAll',
    run: async () => paymentsService.getAll(),
    validate: (data, ctx) => {
      const v = V.validatePaymentsList(data);
      if (!v.valid) return v;
      const found = Array.isArray(data) && data.find(p => p.id === ctx.paymentId);
      if (found) {
        v.errors.push('Pagamento customizado deletado ainda aparece na lista');
        v.valid = false;
      }
      const healthCheck = Array.isArray(data) && data.filter(p => p.name?.includes('[HealthCheck]'));
      if (healthCheck && healthCheck.length > 0) {
        v.errors.push(`Encontrado(s) ${healthCheck.length} pagamento(s) de teste residual (\"[HealthCheck]\") após cleanup`);
        v.valid = false;
      }
      return v;
    },
    description: 'Verifica que globais permanecem e o pagamento customizado foi removido',
  },
  {
    id: 'post-dashboard-stats',
    phase: 6,
    phaseName: 'Verificação Pós-Cleanup',
    name: 'GET /dashboard/stats (final)',
    service: 'dashboardService',
    method: 'getStats',
    run: async () => dashboardService.getStats(),
    validate: (data) => V.validateDashboardStats(data),
    description: 'Dashboard stats final - deve refletir remoção',
  },
  {
    id: 'post-invites-getAll',
    phase: 6,
    phaseName: 'Verificação Pós-Cleanup',
    name: 'GET /invites (após cancelar convite)',
    service: 'invitesService',
    method: 'getAll',
    run: async () => invitesService.getAll(),
    validate: (data, ctx) => {
      const v = V.validateUserInvitesList(data);
      if (Array.isArray(data) && data.find(i => i.id === ctx.inviteId)) {
        v.errors.push('Convite cancelado ainda aparece na listagem');
        v.valid = false;
      }
      return v;
    },
    description: 'Verifica que o convite cancelado não aparece mais na listagem',
  },
];

// ============================================================
// EXPORT - Todos os steps em ordem
// ============================================================
export const ALL_TEST_STEPS = [
  ...PHASE_0_PRECHECK,
  ...PHASE_1_CREATE,
  ...PHASE_2_VERIFY,
  ...PHASE_3_UPDATE,
  ...PHASE_4_EXTRAS,
  ...PHASE_5_CLEANUP,
  ...PHASE_6_POST_CLEANUP,
];

export const PHASE_NAMES = {
  0: 'Pré-verificação (Estado Inicial)',
  1: 'Criação de Entidades',
  2: 'Verificação Pós-Criação',
  3: 'Atualização de Entidades',
  4: 'Operações Adicionais',
  5: 'Limpeza (Exclusão)',
  6: 'Verificação Pós-Cleanup',
};

export const TOTAL_PHASES = 7;
